import { useGCashDefaultMarketAccount } from '@src/hooks/useGCashDefaultMarketAccount';
import {
  toBigWithDefault,
  useBalancesContext,
  useCurrenciesContext,
  useCurrencyConversionRateValue,
  useObservableValue,
  useWLHomeCurrency,
  useWLOrgConfigContext,
} from '@talos/kyoko';
import Big from 'big.js';
import { isNumber } from 'lodash-es';
import { createContext, useContext, useMemo } from 'react';
import { map } from 'rxjs';

type HomeBalanceContextValue = {
  homeCurrency: string | undefined;
  homeCurrencyObject: { Symbol: string; Description: string } | undefined;
  homeBalance: string | undefined;
  displayCurrencyBalance: string | undefined;
  isLoaded: boolean;
};

const HomeBalanceContext = createContext<HomeBalanceContextValue>({
  homeCurrency: undefined,
  homeCurrencyObject: undefined,
  homeBalance: undefined,
  displayCurrencyBalance: undefined,
  isLoaded: false,
});

export function useHomeBalanceContext() {
  return useContext(HomeBalanceContext);
}

export function GCashHomeBalanceProvider({ children }: { children: React.ReactNode }) {
  const { homeCurrency, homeCurrencyObject } = useWLHomeCurrency();
  const { balancesByMarketAccountCurrency } = useBalancesContext();
  const { currenciesBySymbol } = useCurrenciesContext();
  const favoredPairCurrency = useWLOrgConfigContext().config.favoredPairCurrency;
  const defaultMarketAccount = useGCashDefaultMarketAccount();
  // Compute the total balance in the favored pair currency, (USDT)
  const displayCurrencyBalance = useObservableValue<string | undefined>(
    () =>
      balancesByMarketAccountCurrency.pipe(
        map(balancesByMarketAccountCurrency => {
          let sum = Big(0);
          let dp = '4';
          for (const balance of balancesByMarketAccountCurrency.get(defaultMarketAccount)?.values() ?? []) {
            sum = sum.plus(toBigWithDefault(balance.Equivalent?.Amount, 0));
            dp = currenciesBySymbol.get(balance.Currency)?.DefaultIncrement ?? dp;
          }
          return sum.toFixed(isNumber(dp) ? dp : 4);
        })
      ),
    [balancesByMarketAccountCurrency, currenciesBySymbol, defaultMarketAccount],
    undefined
  );

  // Perform currency conversion between displayCurrencyBalance and homeCurrency
  const homeCurrencyToFavoredPairCurrencyConversionRate = useCurrencyConversionRateValue(
    homeCurrency,
    favoredPairCurrency
  );

  const homeBalance = useMemo(() => {
    return homeCurrencyToFavoredPairCurrencyConversionRate?.Rate
      ? toBigWithDefault(displayCurrencyBalance, 0).div(homeCurrencyToFavoredPairCurrencyConversionRate.Rate).toFixed(2)
      : undefined;
  }, [displayCurrencyBalance, homeCurrencyToFavoredPairCurrencyConversionRate]);

  //  there's a rate between homeCurrency and favoredPairCurrency
  const rateExistsBetweenHomeCurrencyAndFavoredPairCurrency =
    !!homeCurrencyToFavoredPairCurrencyConversionRate && !!favoredPairCurrency;
  const isLoaded = rateExistsBetweenHomeCurrencyAndFavoredPairCurrency;

  const value = useMemo(
    () => ({
      homeCurrency,
      homeCurrencyObject,
      homeBalance,
      displayCurrencyBalance,
      isLoaded: isLoaded,
    }),
    [homeCurrency, homeCurrencyObject, homeBalance, displayCurrencyBalance, isLoaded]
  );

  return <HomeBalanceContext.Provider value={value}>{children}</HomeBalanceContext.Provider>;
}
